import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Row, Col, Panel } from 'react-bootstrap';
import { loadOperationsAnalyticsData } from './actions';
import Filter from './Filter';
import moment from 'moment';
import { loadUnpagedTerms, getDefaultActiveTerm, getLastTerm } from '../../terms/actions';
import authUtils from '../../../auth/utils';
import Stats from './Stats';
import LessonsByGrade from './LessonsByGrade';
import LessonsInPeriod from './LessonsInPeriod';
import ChildrenByGrade from './ChildrenByGrade';
import NewChildrenInPeriod from './NewChildrenInPeriod';
import { withTranslation } from 'react-i18next';
import globalize from '../../../common/services/globalize';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartColumn } from '@fortawesome/free-solid-svg-icons';
import { renderExportButton } from '../utils';

class OperationsDashboard extends Component {
	constructor(props) {
		super(props);
		this.handleFilterApply = this.handleFilterApply.bind(this);
		this.formatDataForCsv = this.formatDataForCsv.bind(this);
		this.state = {
			...this.getDefaultFilter()
		};
	}

	componentDidMount() {
		const onTermsLoaded = () => {
			const { activeTermId, activeSchoolYearId } = this.props;

			if (activeTermId && activeSchoolYearId) {
				this.setState({
					termId: activeTermId,
					schoolYearId: activeSchoolYearId
				}, () => this.reloadData());
			}
		};

		this.props.loadUnpagedTerms(null, onTermsLoaded, onTermsLoaded);
	}

	getDefaultFilter() {
		const { activeTermId, activeSchoolYearId } = this.props;

		const today = moment().startOf('date');
		return {
			periodFrom: today.clone().subtract(1, 'month'),	// periodFrom and periodTo will be used if active term is not loaded yet
			periodTo: today,
			termId: activeTermId,							// termId and schoolYearId will be used if active term is already loaded
			schoolYearId: activeSchoolYearId,
			centerIds: null,
			lessonTypeId: 1
		};
	};

	reloadData() {
		const { periodFrom, periodTo, termId, schoolYearId, centerIds, lessonTypeId } = this.state;
		const statsRequestData = {
			periodFrom: periodFrom.startOf('day').format(),
			periodTo: periodTo.endOf('day').format(),
			termId,
			schoolYearId,
			centerIds: centerIds || [],
			lessonTypeId,
			timeZone: globalize.getCurrentTimeZone(this.props.franchisor)
		};

		const { loadOperationsAnalyticsData } = this.props;
		loadOperationsAnalyticsData('dashboardStats', statsRequestData);
		loadOperationsAnalyticsData('lessonsByCategory', statsRequestData);
		loadOperationsAnalyticsData('lessonsInPeriod', statsRequestData);
	}

	handleFilterApply(filter) {
		this.setState({ ...filter }, () => this.reloadData());
	}

	formatDataForCsv(data, valueField) {
		if (!["value", "newChildren"].includes(valueField)) {
			throw new Error(`Invalid valueField: ${valueField}. Use "value" or "newChildren".`);
		}
		// Extract all unique grade names from the time series data
		const allGrades = Array.from(
			new Set(data.flatMap(d => d.grades.map(g => g.name.trim())))
		);

		return data.map(item => {
			const row = { timestamp: item.timestamp };
			allGrades.forEach(gradeName => {
				const grade = item.grades.find(g => g.name.trim() === gradeName);
				row[gradeName] = grade ? grade[valueField] : 0;
			});
			return row;
		});
	}

	render() {
		const { currentUser,
			terms,
			schoolYears,
			analytics: {
				dashboardStats,
				lessonsByCategory = {
					loading: false, data: null
				},
				lessonsInPeriod
			},
			franchisor,
			t
		} = this.props;

		const lessonsByGrade = {
			loading: lessonsByCategory.loading,
			data: lessonsByCategory.data && lessonsByCategory.data.grades
		};

		const canViewOperationsReports = authUtils.hasPermission(currentUser, 'OperationsDashboard');

		return <div>
			{canViewOperationsReports && <div className='dashboard'>
				<Row className='mb-1'>
					<Col xs={12}>
						<Filter currentCulture={globalize.getCurrentCulture(franchisor)} defaultFilterValues={this.getDefaultFilter()}
							periodFrom={this.state.periodFrom} periodTo={this.state.periodTo}
							termId={this.state.termId} terms={terms}
							centerIds={this.state.centerIds}
							lessonTypeId={this.state.lessonTypeId}
							schoolYearId={this.state.schoolYearId} schoolYears={schoolYears} activeSchoolYearId={this.props.activeSchoolYearId}
							onApplyFilter={this.handleFilterApply}
						/>
					</Col>
				</Row>
				<Stats {...dashboardStats} schoolYearId={this.state.schoolYearId} />
				<Row>
					<Col sm={6}>
						<Panel>
							<Panel.Heading><FontAwesomeIcon icon={faChartColumn} />{t('OperationsDashboard_ClassesForPeriod')}
								{lessonsInPeriod && lessonsInPeriod.data && renderExportButton(this.formatDataForCsv(lessonsInPeriod.data, 'value'), 'ClassesForPeriod.csv', t)}
							</Panel.Heading>
							<Panel.Body>
								<LessonsInPeriod {...lessonsInPeriod} />
							</Panel.Body>
						</Panel>
					</Col>
					<Col sm={6}>
						<Panel>
							<Panel.Heading><FontAwesomeIcon icon={faChartColumn} />{t('OperationsDashboard_NewChildrenForPeriod')}
								{lessonsInPeriod && lessonsInPeriod.data && renderExportButton(this.formatDataForCsv(lessonsInPeriod.data, 'newChildren'), 'NewChildrenForPeriod.csv', t)}
							</Panel.Heading>
							<Panel.Body>
								<NewChildrenInPeriod {...lessonsInPeriod} />
							</Panel.Body>
						</Panel>
					</Col>
				</Row>
				<Row>
					<Col sm={6}>
						<Panel>
							<Panel.Heading><FontAwesomeIcon icon={faChartColumn} />{t('OperationsDashboard_ClassesByGrade')}
								{lessonsByGrade && lessonsByGrade.data && renderExportButton(lessonsByGrade.data, 'ClassesByGrade.csv', t)}
							</Panel.Heading>
							<Panel.Body>
								<LessonsByGrade {...lessonsByGrade} />
							</Panel.Body>
						</Panel>
					</Col>
					<Col sm={6}>
						<Panel>
							<Panel.Heading><FontAwesomeIcon icon={faChartColumn} />{t('OperationsDashboard_ChildrenByGrade')}
								{lessonsByGrade && lessonsByGrade.data && renderExportButton(lessonsByGrade.data, 'ChildrenByGrade.csv', t)}
							</Panel.Heading>
							<Panel.Body>
								<ChildrenByGrade {...lessonsByGrade} />
							</Panel.Body>
						</Panel>
					</Col>

				</Row>
			</div>}
		</div>;


	}
}

const mapStateToProps = (state) => {
	const {
		analytics: { operations },
		auth: { currentUser },
		entities: { terms },
		lookups: { schoolYears },
		configuration: { franchisor }
	} = state;

	let activeTerm = getDefaultActiveTerm(terms);
	if (!activeTerm) {
		activeTerm = getLastTerm(terms);
	}

	return {
		analytics: operations,
		currentUser,
		schoolYears,
		terms,
		activeSchoolYearId: activeTerm && activeTerm.schoolYearId,
		activeTermId: activeTerm && activeTerm.id,
		franchisor
	};
};

export default connect(mapStateToProps, {
	loadOperationsAnalyticsData,
	loadUnpagedTerms
})(withTranslation()(OperationsDashboard));